/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Creates a standardized NextResponse for application errors with appropriate HTTP status codes</blockquote>
 * 
 * @param {ApplicationErrorResponse} errorResponse - The application error response to handle
 * @param {string} operation - Description of the operation that failed
 * @returns {NextResponse<ApplicationErrorResponse>} NextJS response object with error details
 * 
 * @example
 * ```typescript
 * const errorResponse = analyzeApplicationError('User not found', 'User does not exist', 'USER_NOT_FOUND');
 * return handleApplicationError(errorResponse, 'fetching user data');
 * ```
 */
import { NextResponse } from 'next/server';
import type { ApplicationErrorResponse } from '../../interfaces/ApplicationErrorResponse';

export function handleApplicationError(
  errorResponse: ApplicationErrorResponse,
  operation: string = 'application operation',
): NextResponse<ApplicationErrorResponse> {
  const technical = `An error occurred during ${operation}`;
  errorResponse.technical = technical;

  // Log error for monitoring (in production, this should use @shared/logger)
  console.error(`Application error during ${operation}:`, { error: errorResponse });

  return NextResponse.json(errorResponse, { status: errorResponse.statusCode });
} 